import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

type Mode = 'sandbox' | 'production';

interface PaymentSettingsResponse {
  success: boolean;
  data?: {
    provider: 'razorpay';
    mode: Mode;
    is_active: boolean;
    sandbox_key_id: string;
    sandbox_key_secret_exists?: boolean;
    production_key_id: string;
    production_key_secret_exists?: boolean;
    updated_by: number | null;
    updated_at: string | null;
  };
  message?: string;
}

const PaymentSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [mode, setMode] = useState<Mode>('sandbox');
  const [isActive, setIsActive] = useState(true);
  const [sandboxKeyId, setSandboxKeyId] = useState('');
  const [sandboxKeySecret, setSandboxKeySecret] = useState('');
  const [productionKeyId, setProductionKeyId] = useState('');
  const [productionKeySecret, setProductionKeySecret] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; details?: string } | null>(null);
  const [showSecrets, setShowSecrets] = useState<{ sandbox?: boolean; production?: boolean }>({});
  const [sandboxSecretExists, setSandboxSecretExists] = useState(false);
  const [productionSecretExists, setProductionSecretExists] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get<PaymentSettingsResponse>('/admin/payment-settings');
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        setMode(d.mode);
        setIsActive(d.is_active);
        setSandboxKeyId(d.sandbox_key_id || '');
        setProductionKeyId(d.production_key_id || '');
        setSandboxSecretExists(d.sandbox_key_secret_exists || false);
        setProductionSecretExists(d.production_key_secret_exists || false);
      }
    } catch (e) {
      // noop UI already basic
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      const res = await axios.put('/admin/payment-settings/razorpay', {
        mode,
        sandbox_key_id: sandboxKeyId,
        sandbox_key_secret: sandboxKeySecret || undefined,
        production_key_id: productionKeyId,
        production_key_secret: productionKeySecret || undefined,
        is_active: isActive
      });
      if (res.data?.success) {
        alert('Saved. Razorpay reinitialized.');
        // clear secrets from state after save for safety
        setSandboxKeySecret('');
        setProductionKeySecret('');
        loadSettings();
      }
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post('/admin/payment-settings/razorpay/test', {
        mode,
        sandbox_key_id: sandboxKeyId || undefined,
        sandbox_key_secret: sandboxKeySecret || undefined,
        production_key_id: productionKeyId || undefined,
        production_key_secret: productionKeySecret || undefined
      });
      setTestResult({ success: true, message: res.data?.message || 'Connection successful' });
    } catch (e: any) {
      setTestResult({ success: false, message: e?.response?.data?.message || 'Connection failed', details: e?.response?.data?.details });
    } finally {
      setTesting(false);
    }
  };

  const Header = useMemo(() => (
    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
      <div>
        <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Payment Settings</h4>
        <p className="text-muted mb-0">Configure Razorpay keys and mode</p>
      </div>
    </div>
  ), []);

  return (
    <div>
      <div className="row mb-4">
        <div className="col-12">
          {Header}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : (
        <div className="row">
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body">
                  <h5 className="mb-3">Razorpay</h5>

                  <div className="row g-3 align-items-end">
                    <div className="col-md-4">
                      <label className="form-label">Mode</label>
                      <select className="form-select" value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
                        <option value="sandbox">Sandbox</option>
                        <option value="production">Production</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Status</label>
                      <select className="form-select" value={isActive ? 'active' : 'inactive'} onChange={(e) => setIsActive(e.target.value === 'active')}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="mb-3">Sandbox Keys</h6>
                      <div className="mb-3">
                        <label className="form-label">Key ID</label>
                        <input className="form-control" value={sandboxKeyId} onChange={(e) => setSandboxKeyId(e.target.value)} placeholder="rzp_test_xxx" />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Key Secret {sandboxSecretExists && <span className="badge bg-success ms-2">Saved</span>}</label>
                        <div className="input-group">
                          <input type={showSecrets.sandbox ? 'text' : 'password'} className="form-control" value={sandboxKeySecret} onChange={(e) => setSandboxKeySecret(e.target.value)} placeholder={sandboxSecretExists ? "Enter new secret to update" : "Enter key secret"} />
                          <button className="btn btn-outline-secondary" type="button" onClick={() => setShowSecrets(s => ({ ...s, sandbox: !s.sandbox }))}>
                            <i className={`bx ${showSecrets.sandbox ? 'bx-hide' : 'bx-show'}`}></i>
                          </button>
                        </div>
                        <small className="text-muted">{sandboxSecretExists ? "Current secret is saved. Enter new secret to update, or leave blank to keep existing" : "Enter your Razorpay key secret"}</small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6 className="mb-3">Production Keys</h6>
                      <div className="mb-3">
                        <label className="form-label">Key ID</label>
                        <input className="form-control" value={productionKeyId} onChange={(e) => setProductionKeyId(e.target.value)} placeholder="rzp_live_xxx" />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Key Secret {productionSecretExists && <span className="badge bg-success ms-2">Saved</span>}</label>
                        <div className="input-group">
                          <input type={showSecrets.production ? 'text' : 'password'} className="form-control" value={productionKeySecret} onChange={(e) => setProductionKeySecret(e.target.value)} placeholder={productionSecretExists ? "Enter new secret to update" : "Enter key secret"} />
                          <button className="btn btn-outline-secondary" type="button" onClick={() => setShowSecrets(s => ({ ...s, production: !s.production }))}>
                            <i className={`bx ${showSecrets.production ? 'bx-hide' : 'bx-show'}`}></i>
                          </button>
                        </div>
                        <small className="text-muted">{productionSecretExists ? "Current secret is saved. Enter new secret to update, or leave blank to keep existing" : "Enter your Razorpay key secret"}</small>
                      </div>
                    </div>
                  </div>

                  {testResult && (
                    <div className={`alert alert-${testResult.success ? 'success' : 'danger'} mt-3`}>
                      <strong>{testResult.success ? 'Success' : 'Failed'}:</strong> {testResult.message}
                      {testResult.details && <div className="small mt-1">{testResult.details}</div>}
                    </div>
                  )}

                  <div className="d-flex gap-2 mt-4">
                    <button className="btn btn-info" onClick={handleTest} disabled={testing}>
                      {testing ? (<><span className="spinner-border spinner-border-sm me-1"></span>Testing...</>) : (<>Test Connection</>)}
                    </button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                      {saving ? (<><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>) : (<>Save & Apply</>)}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default PaymentSettings;


